import { FC, ReactNode, useEffect, useState } from 'react';
import { Badge, Button, Col, Dropdown, Menu, Row, Tag } from 'antd';
import information from 'manifest.json';
import {
    TbAlertTriangle,
    TbArrowUpRight,
    TbCircleArrowUp,
    TbCreditCard,
    TbInfoCircle,
    TbLayoutGrid,
    TbLayoutGridAdd,
    TbLifebuoy,
    TbMessage2,
    TbSettings,
    TbUserPlus,
} from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useCASL } from '@/hooks/useCASL';
import { useEntitlements } from '@/hooks/useEntitlements';
import { openNotionFormModal, PlanTag, SmallTooltip } from '@/shared';
import { PlatformAlert } from '@/shared/components/alerts';
import useAccountSwitcherItems from '@/shared/components/dropdowns/AccountSwitcherItems';
import AccountWorkspaceSection from '@/shared/components/dropdowns/AccountSwitcherItems/AccountWorkspaceSection';
import CountercyclicalDropdownFooter from '@/shared/components/dropdowns/CountercyclicalDropdownFooter';
import DropdownPanel, { DropdownPanelMenu } from '@/shared/components/dropdowns/DropdownPanel';
import InviteToWorkspaceModal from '@/shared/components/modals/GlobalModals/InviteToWorkspaceModal';
import JoinOrCreateWorkspaceModal from '@/shared/components/modals/GlobalModals/JoinOrCreateWorkspaceModal';
import { useUserStore } from '@/stores';
import { useInvitationStore } from '@/stores/database/aliases/invitations';
import { dayjs } from '@/utilities';

interface WorkspacesDropdownProps {
    children?: ReactNode;
    disabled?: boolean;
}

const WorkspacesDropdown: FC<WorkspacesDropdownProps> = ({ children, disabled }): JSX.Element => {
    const accountItems = useAccountSwitcherItems({ section: 'actions', showStatus: false });
    const { selectedUser: user } = useUserStore();
    const { planTier } = useEntitlements();

    const { invitations, fetchInvitations } = useInvitationStore();

    useEffect(() => {
        void fetchInvitations();
    }, [fetchInvitations]);

    const { token } = useAntDesignTheme();
    const { canInviteUsers } = useCASL();

    const navigate = useNavigate();

    // const [_, setWorkspace] = useCurrentWorkspace();

    // const { selectedWorkspace: workspace } = useWorkspaceStore();

    const [inviteToWorkspaceOpen, setInviteToWorkspaceOpen] = useState(false);

    // ! This probably needs to get refactored into a hook
    const daysDifference = dayjs().diff(user?.createdAt, 'days');
    // Calculate the remaining days until 30 days have passed
    const remainingDays = information.freeTrialDayLength - daysDifference;

    const [workspaceModalOpen, setWorkspaceModalOpen] = useState(false);

    return (
        <>
            <Dropdown
                destroyOnHidden
                trigger={['click']}
                placement='bottomRight'
                popupRender={() => (
                    <DropdownPanel
                        minWidth={16 * 24}
                        maxHeight='none'
                        contentScrollDisabled
                        style={{ marginTop: '0.75rem' }}
                        header={
                            <DropdownPanelMenu disabled={disabled} style={{ padding: 0 }}>
                                <Row>
                                    <Col span={22}>
                                        <small style={{ color: token.colorTextTertiary }}>
                                            <TbLayoutGrid
                                                style={{
                                                    verticalAlign: '-0.125rem',
                                                    marginLeft: '0.85rem',
                                                    marginRight: '0.25rem',
                                                }}
                                            />
                                            Workspaces
                                        </small>
                                    </Col>
                                    <Col span={2} style={{ textAlign: 'right' }}>
                                        <small>
                                            <SmallTooltip
                                                title={
                                                    <>
                                                        Learn about Workspaces in our{' '}
                                                        <a
                                                            href='https://docs.countercyclical.io/guides/creating-an-account#workspace'
                                                            style={{ color: token.blue5 }}
                                                            target='_blank'
                                                            rel='noreferrer'
                                                        >
                                                            docs
                                                            <TbArrowUpRight style={{ verticalAlign: '-0.125rem' }} />
                                                        </a>
                                                    </>
                                                }
                                                placement='right'
                                                padding='0 0.75rem'
                                            >
                                                <TbInfoCircle
                                                    style={{
                                                        verticalAlign: '-0.125rem',
                                                        marginRight: '0.85rem',
                                                        color: token.colorTextTertiary,
                                                    }}
                                                />
                                            </SmallTooltip>
                                        </small>
                                    </Col>
                                </Row>
                                {planTier !== 'enterprise' && (
                                    <>
                                        <Menu.Divider style={{ margin: 0 }} />
                                        <Menu.Item
                                            key='planBasedMenuItem'
                                            style={{ marginBottom: 0 }}
                                            icon={<TbCircleArrowUp size='1rem' />}
                                            title={null}
                                            onClick={() => {
                                                navigate('/settings/account/plans');
                                            }}
                                        >
                                            <Row>
                                                <Col span={12}>Upgrade Plan</Col>
                                                <Col span={12} style={{ textAlign: 'right' }}>
                                                    <PlanTag plan={planTier === 'free' ? 'pro' : 'enterprise'} />
                                                </Col>
                                            </Row>
                                        </Menu.Item>
                                    </>
                                )}
                            </DropdownPanelMenu>
                        }
                        actions={
                            <DropdownPanelMenu disabled={disabled} style={{ padding: 0, paddingBottom: 0 }}>
                                {accountItems}
                                <Menu.Divider />
                                <Menu.Item
                                    icon={<TbUserPlus size={'1rem'} />}
                                    key='inviteMembersToWorkspace'
                                    title={null}
                                    disabled={!canInviteUsers()}
                                    onClick={() => {
                                        if (!canInviteUsers()) {
                                            return;
                                        }

                                        setInviteToWorkspaceOpen(true);
                                    }}
                                >
                                    Invite to Workspace
                                </Menu.Item>
                                <Menu.Item
                                    key='billing'
                                    icon={<TbCreditCard size={'1rem'} />}
                                    title={null}
                                    onClick={() => navigate('/settings/account/plans/billing')}
                                >
                                    Plans & Billing
                                </Menu.Item>
                                <Menu.Item
                                    key='workspaceSettings'
                                    icon={<TbSettings size={'1rem'} />}
                                    disabled={disabled}
                                    title={null}
                                    onClick={() => navigate('/settings/workspace/general')}
                                >
                                    Workspace Settings
                                </Menu.Item>
                                <Menu.Divider />
                                <Menu.Item
                                    icon={<TbLayoutGridAdd size={'1rem'} />}
                                    key='joinOrCreateAWorkspace'
                                    title={null}
                                    onClick={() => {
                                        // navigate('/workspaces');
                                        setWorkspaceModalOpen(true);
                                    }}
                                >
                                    <Row wrap={false} align='middle'>
                                        <Col flex='auto'>Join or Create a Workspace</Col>
                                        {invitations?.length !== 0 && (
                                            <Col flex='none'>
                                                <SmallTooltip
                                                    title={`${invitations?.length} Workspace Invitations`}
                                                    placement='right'
                                                >
                                                    <Tag
                                                        color='blue'
                                                        style={{ borderColor: 'transparent', marginRight: 0 }}
                                                    >
                                                        <Badge
                                                            dot
                                                            color='blue'
                                                            style={{ marginRight: '0.5rem' }}
                                                            status='processing'
                                                        />
                                                        {invitations?.length}
                                                    </Tag>
                                                </SmallTooltip>
                                            </Col>
                                        )}
                                    </Row>
                                </Menu.Item>
                                {/* <Menu.Item key='addAnAccount' title={null} icon={<TbPlus size='1rem' />}>
                            Add Account
                        </Menu.Item>
                        <Menu.Item key='logOutOfAllAccounts' title={null} icon={<TbCircleX size='1rem' />}>
                            Log Out of All Accounts
                        </Menu.Item> */}
                                <Menu.Divider />
                                <Menu.Item
                                    title={null}
                                    key='submitFeedback'
                                    icon={<TbMessage2 size={'1rem'} />}
                                    onClick={() => openNotionFormModal('feedback')}
                                >
                                    Submit Feedback
                                </Menu.Item>
                                <Menu.Item
                                    title={null}
                                    key='troubleshooting'
                                    icon={<TbAlertTriangle size={'1rem'} />}
                                    onClick={() => {
                                        window.open('https://docs.countercyclical.io/extras/troubleshooting', '_blank');
                                    }}
                                >
                                    Troubleshooting
                                    <TbArrowUpRight
                                        style={{
                                            color: token.colorTextTertiary,
                                            verticalAlign: '-0.125rem',
                                            marginLeft: '0.375rem',
                                        }}
                                    />
                                </Menu.Item>
                                <Menu.Item
                                    title={null}
                                    key='contactSupport'
                                    icon={<TbLifebuoy size={'1rem'} />}
                                    onClick={() => openNotionFormModal('support')}
                                >
                                    Support
                                </Menu.Item>
                                {/* {(user?.plan === 'Enterprise' ||
                        user?.plan === UserPlan.PRO_MONTHLY ||
                        user?.plan === UserPlan.PRO_ANNUALLY) && (
                            <>
                                <Menu.Item
                                    disabled={disabledFeatures}
                                    icon={<TbTrees size={'1rem'} />}
                                    onClick={() => {
                                        window.open('https://community.countercyclical.io');
                                    }}
                                    key="exploreTheCommunity"
                                    title={null}
                                >
                                    <Row>
                                        <Col span={12}>Explore the Community</Col>
                                        <Col span={12} style={{ textAlign: 'right' }}>
                                            <SmallTooltip
                                                overlayStyle={{ display: 'none' }} // for now
                                                padding="0 1rem"
                                                placement="right"
                                                title={
                                                    <>
                                                        <TbWorldShare
                                                            size="1rem"
                                                            style={{ verticalAlign: '-0.2rem', marginRight: '0.25rem' }}
                                                        />
                                                        <span style={{ verticalAlign: '0.05rem' }}>
                                                            community.countercyclical.io
                                                        </span>
                                                    </>
                                                }
                                            >
                                                <TbWorldShare size="1rem" style={{ verticalAlign: '-0.15rem' }} />
                                            </SmallTooltip>
                                        </Col>
                                    </Row>
                                </Menu.Item>
                                <Menu.Divider />
                            </>
                        ) } */}
                                {planTier === 'free' && (
                                    <>
                                        <Menu.Divider style={{ marginBottom: 0 }} />
                                        <PlatformAlert
                                            style={{ paddingRight: '0.5rem' }}
                                            action={
                                                <Button
                                                    size='small'
                                                    type='dashed'
                                                    icon={<TbCircleArrowUp style={{ verticalAlign: '-0.125rem' }} />}
                                                    onClick={() => {
                                                        navigate('/settings/account/plans');
                                                    }}
                                                >
                                                    Upgrade
                                                </Button>
                                            }
                                            type={remainingDays > 0 ? 'warning' : 'error'}
                                            banner
                                            showIcon
                                            message={
                                                remainingDays > 0 ? (
                                                    <small>
                                                        Trial Ending in <strong>{remainingDays}</strong> Days
                                                    </small>
                                                ) : (
                                                    <small>Trial Ended</small>
                                                )
                                            }
                                        />
                                    </>
                                )}
                                <CountercyclicalDropdownFooter flushTop />
                            </DropdownPanelMenu>
                        }
                    >
                        <AccountWorkspaceSection disabled={disabled} />
                    </DropdownPanel>
                )}
            >
                <span>{children}</span>
            </Dropdown>
            <JoinOrCreateWorkspaceModal open={workspaceModalOpen} setOpen={setWorkspaceModalOpen} />
            <InviteToWorkspaceModal open={inviteToWorkspaceOpen} setOpen={setInviteToWorkspaceOpen} />
        </>
    );
};

export default WorkspacesDropdown;
